import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { getMemoryUsage } from 'src/utils/miscUtils';
import { Feature } from 'src/WebApiController';

const MEMORY_THRESHOLD = 0.85; // Absolute max threshold (for local testing, set this to 0.01)

// Hook for memory cleanup
export const useMemoryCheckup = () => {
  const queryClient = useQueryClient();
  const [memoryOverThresholdExceeded, setMemoryOverThresholdExceeded] =
    useState(undefined as undefined | [number, number]); // [currentUsageRatio, threshold]

  const hasAutoCleanupBrowserDataFeature = useUserHasFeature(
    Feature.AutoCleanupBrowserData
  );

  const performMemoryCleanup = useCallback(() => {
    console.log('🧹 Performing emergency memory cleanup...');

    try {
      // Clear react-query cache
      queryClient.clear();
      queryClient.removeQueries();

      // 2. Clear browser caches (Service Worker, Cache API)
      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            if (!cacheName.includes('critical')) {
              // Preserve critical caches
              caches.delete(cacheName);
            }
          });
        });
        console.log('✅ Browser caches cleared');
      }

      setMemoryOverThresholdExceeded(undefined);
      console.log('🎉 Memory cleanup completed');
    } catch (error) {
      console.error('❌ Error during memory cleanup:', error);
    }
  }, [queryClient]);

  useEffect(() => {
    if (!hasAutoCleanupBrowserDataFeature) return;

    const checkMemoryUsage = () => {
      const memoryUsage = getMemoryUsage();

      if (memoryUsage.usedJSHeapSize && memoryUsage.jsHeapSizeLimit) {
        const usageRatio =
          memoryUsage.usedJSHeapSize / memoryUsage.jsHeapSizeLimit;
        const usedMB = memoryUsage.usedJSHeapSize / 1024 / 1024;
        const limitMB = memoryUsage.jsHeapSizeLimit / 1024 / 1024;

        if (usageRatio > MEMORY_THRESHOLD) {
          console.error(
            `🚨 EMERGENCY: Critical memory usage: ${(usageRatio * 100).toFixed(1)}%`
          );
          console.error(
            `Used: ${usedMB.toFixed(1)}MB of ${limitMB.toFixed(1)}MB`
          );
          if (
            !memoryOverThresholdExceeded ||
            usageRatio !== memoryOverThresholdExceeded[0]
          ) {
            setMemoryOverThresholdExceeded([usageRatio, MEMORY_THRESHOLD]);
          }
        } else {
          if (memoryOverThresholdExceeded) {
            setMemoryOverThresholdExceeded(undefined);
          }
        }

        // Optional: Log current usage periodically for monitoring
        if (Math.random() < 0.1) {
          // 10% chance to log current usage
          console.debug(
            `📊 Memory usage: ${(usageRatio * 100).toFixed(1)}% (${usedMB.toFixed(1)}MB/${limitMB.toFixed(1)}MB)`
          );
        }
      }
    };

    // Check memory every 30 seconds
    const interval = setInterval(checkMemoryUsage, 30000);

    // Also check on certain browser events
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkMemoryUsage();
      }
    };

    const handleFocus = () => {
      checkMemoryUsage();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [
    hasAutoCleanupBrowserDataFeature,
    memoryOverThresholdExceeded,
    performMemoryCleanup,
  ]);

  return {
    performMemoryCleanup,
    memoryOverThresholdExceeded:
      hasAutoCleanupBrowserDataFeature && memoryOverThresholdExceeded,
  };
};
